import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { t, font, Button, Input, Panel, Toggle, Badge, Avatar, ProgressBar } from "@realtyflows/ui";
import { getBillingStatus, createCheckout, createBillingPortal, updateProfile, uploadPhoto, sendVerificationCode, verifyVerificationCode, changeEmail, saveToken, getPaymentMethod, getSubscription, resumeSubscription, cancelSubscription, changePlan, purchaseSignings } from "../api";
import CodeInput from "../components/CodeInput";
import Automations from "./Automations";
import PipelineSettings from "./PipelineSettings";
import useResponsive from "../hooks/useResponsive";
import useScrollToAlert from "../hooks/useScrollToError";

const planConfig = {
  free: { label: "Free", price: 0, category: "Solo" },
  starter: { label: "Starter", price: 39, category: "Solo" },
  pro: { label: "Pro", price: 89, category: "Solo" },
  team_starter: { label: "Team Starter", price: 249, category: "Team" },
  team_pro: { label: "Team Pro", price: 549, category: "Team" },
  agency: { label: "Agency", price: 999, category: "Team" },
  enterprise: { label: "Enterprise", price: null, category: "Team" },
};

const tierLevel = { free: 0, starter: 1, pro: 2, team_starter: 3, team_pro: 4, agency: 5, enterprise: 6 };

const planFeatures = {
  free: ["1 signing", "1 user", "Unlimited CRM", "Pipeline view", "Email support"],
  starter: ["3 signings/mo", "1 user", "Basic AI", "Pipeline view", "Email support"],
  pro: ["10 signings/mo", "1 user", "Full AI", "Smart automation", "Priority email support"],
  team_starter: ["20 signings/mo", "5 users included", "Full AI", "Team management", "+$29/extra seat"],
  team_pro: ["50 signings/mo", "15 users included", "Advanced AI", "Brokerage pipeline", "+$29/extra seat"],
  agency: ["100 signings/mo", "50 users included", "Advanced AI + White Label", "Custom branding", "+$19/extra seat"],
  enterprise: ["Custom signings", "Custom users", "Custom AI", "API & webhooks", "24/7 support"],
};

function BillingMenu({ billing, navigate }) {
  const [open, setOpen] = useState(false);
  const isActive = billing.subscriptionStatus === "active" || billing.subscriptionStatus === "cancelling";
  const items = [
    ...(isActive ? [
      { label: "Update Payment Method", icon: "\uD83D\uDCB3", action: () => navigate("/billing?tab=payment") },
      { label: "View Invoices", icon: "\uD83E\uDDFE", action: () => navigate("/billing?tab=invoices") },
    ] : []),
    { label: "View All Plans", icon: "\u2B50", action: () => navigate("/billing?tab=plans") },
    ...(isActive && !billing.subscriptionStatus?.includes("cancelling") ? [
      { divider: true },
      { label: "Cancel Plan", icon: "\u274C", action: () => navigate("/billing?tab=cancel"), danger: true },
    ] : []),
  ];
  return (
    <div style={{ position: "relative" }}>
      <div onClick={() => setOpen(!open)} style={{
        width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", background: open ? t.surfaceWarm : "transparent", border: `1px solid ${open ? t.border : "transparent"}`,
        transition: "all 0.15s",
      }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = t.surfaceWarm; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = "transparent"; }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="3" r="1.5" fill={t.textSecondary} />
          <circle cx="8" cy="8" r="1.5" fill={t.textSecondary} />
          <circle cx="8" cy="13" r="1.5" fill={t.textSecondary} />
        </svg>
      </div>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setOpen(false)} />
          <div style={{
            position: "absolute", top: 34, right: 0, background: t.surface,
            border: `1.5px solid ${t.border}`, borderRadius: 10, padding: "6px 0",
            minWidth: 200, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 100,
            animation: "scaleIn 0.15s ease",
          }}>
            {items.map((item, i) => item.divider ? (
              <div key={i} style={{ height: 1, background: t.border, margin: "4px 0" }} />
            ) : (
              <div key={i} onClick={() => { item.action(); setOpen(false); }} style={{
                padding: "9px 16px", display: "flex", alignItems: "center", gap: 10,
                cursor: "pointer", fontSize: 13, fontFamily: font.body,
                color: item.danger ? t.coral : t.textPrimary,
                transition: "background 0.1s",
              }}
                onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <span style={{ fontSize: 14 }}>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Settings({ user, onUserUpdate, onBillingChange, defaultTab = "general" }) {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Sync activeTab when navigating between /settings and /settings/automations
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingMsg, setBillingMsg] = useState("");
  const billingMsgRef = useScrollToAlert(billingMsg);
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [billingAction, setBillingAction] = useState("");
  const [switchConfirm, setSwitchConfirm] = useState(null); // { plan, label, price }
  const [signingPurchaseConfirm, setSigningPurchaseConfirm] = useState(null); // { key, name, price, quantity }
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: user?.name || "", phone: user?.phone || "", brokerage: user?.brokerage || "" });
  const [profileMsg, setProfileMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState({
    newClient: true, hotClient: true, appointments: true, weekly: false, aiAlerts: true,
  });
  const [emailEditing, setEmailEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailStep, setEmailStep] = useState(1); // 1 = enter email, 2 = enter code
  const [emailMsg, setEmailMsg] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);

  useEffect(() => {
    const status = searchParams.get("billing");
    if (status === "success") setBillingMsg("Payment method added successfully!");
    if (status === "cancelled") setBillingMsg("Your plan will be cancelled at the end of your billing period. Signings and AI features will deactivate then.");

    getBillingStatus()
      .then(b => {
        setBilling(b);
        if (b?.hasPaymentMethod || b?.subscriptionStatus === "active") {
          getPaymentMethod().then(r => setPaymentMethod(r.paymentMethod)).catch(() => {});
          getSubscription().then(r => setSubscription(r.subscription)).catch(() => {});
        }
      })
      .catch(() => setBilling(null))
      .finally(() => setBillingLoading(false));
  }, [searchParams]);

  const handleUpgrade = (plan) => {
    const cfg = planConfig[plan];
    const currentCfg = planConfig[currentPlan];
    const newPrice = cfg.price != null ? (billingInterval === "annual" ? Math.round(cfg.price * 10 / 12) : cfg.price) : null;
    const oldPrice = currentCfg?.price != null ? (billingInterval === "annual" ? Math.round(currentCfg.price * 10 / 12) : currentCfg.price) : 0;
    const isUpgrade = tierLevel[plan] > tierLevel[currentPlan];

    if (billing?.subscriptionStatus === "active") {
      // Show confirmation modal for plan switch
      setSwitchConfirm({ plan, label: cfg.label, newPrice, oldPrice, isUpgrade, isSwitch: true });
    } else {
      // New subscription — show confirmation before Stripe Checkout
      setSwitchConfirm({ plan, label: cfg.label, newPrice, oldPrice: 0, isUpgrade: true, isSwitch: false });
    }
  };

  const handleCheckout = async (plan) => {
    setBillingAction(plan);
    try {
      const result = await createCheckout(plan, billingInterval);
      if (result.reactivated) {
        // Cancelling subscription was reactivated with new plan
        const updated = await getBillingStatus();
        setBilling(updated);
        if (onBillingChange) onBillingChange();
        setBillingMsg(`Reactivated and switched to ${planConfig[plan]?.label || plan} plan!`);
      } else if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      setBillingMsg(err.message || "Failed to start checkout");
    } finally {
      setBillingAction("");
    }
  };

  const handleConfirmSwitch = async () => {
    if (!switchConfirm) return;
    // Downgrade to free = cancel subscription
    if (switchConfirm.plan === "free") {
      setBillingAction("free");
      try {
        await cancelSubscription("Downgraded to free plan");
        const updated = await getBillingStatus();
        setBilling(updated);
        if (onBillingChange) onBillingChange();
        setBillingMsg("Downgraded to Free plan. Your subscription will end at the current billing period.");
        setSwitchConfirm(null);
      } catch (err) {
        setBillingMsg(err.message || "Failed to downgrade");
        setSwitchConfirm(null);
      } finally {
        setBillingAction("");
      }
      return;
    }
    if (!switchConfirm.isSwitch) {
      // New subscription — go to Stripe Checkout
      setSwitchConfirm(null);
      handleCheckout(switchConfirm.plan);
      return;
    }
    setBillingAction(switchConfirm.plan);
    try {
      await changePlan(switchConfirm.plan, billingInterval);
      const updated = await getBillingStatus();
      setBilling(updated);
      if (onBillingChange) onBillingChange();
      setBillingMsg(`${switchConfirm.isUpgrade ? "Upgraded" : "Downgraded"} to ${switchConfirm.label} plan!`);
      setSwitchConfirm(null);
    } catch (err) {
      setBillingMsg(err.message || "Failed to switch plan");
      setSwitchConfirm(null);
    } finally {
      setBillingAction("");
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setProfileMsg("");
    try {
      await updateProfile(profileForm);
      setProfileMsg("Profile updated successfully!");
      setEditing(false);
      setTimeout(() => setProfileMsg(""), 4000);
    } catch (err) {
      setProfileMsg(err.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const { url } = await createBillingPortal();
      if (url) window.location.href = url;
    } catch (err) {
      alert(err.message || "Failed to open billing portal");
    }
  };

  const handleResumeSubscription = async () => {
    setBillingAction("resuming");
    try {
      await resumeSubscription();
      setBillingMsg("Subscription resumed successfully!");
      const b = await getBillingStatus();
      setBilling(b);
      const s = await getSubscription();
      setSubscription(s.subscription);
    } catch (err) {
      setBillingMsg(err.message || "Failed to resume");
    } finally {
      setBillingAction("");
    }
  };


  // Email cooldown timer
  useEffect(() => {
    if (emailCooldown <= 0) return;
    const timer = setTimeout(() => setEmailCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [emailCooldown]);

  const handleSendEmailCode = async () => {
    setEmailMsg("");
    setEmailLoading(true);
    try {
      await sendVerificationCode({ email: newEmail, purpose: "change_email" });
      setEmailStep(2);
      setEmailCooldown(60);
    } catch (err) {
      setEmailMsg(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyAndChangeEmail = async () => {
    setEmailMsg("");
    if (emailCode.length !== 6) { setEmailMsg("Please enter the 6-digit code"); return; }
    setEmailLoading(true);
    try {
      await verifyVerificationCode({ email: newEmail, code: emailCode, purpose: "change_email" });
      const { token, user: updatedUser } = await changeEmail({ newEmail, code: emailCode });
      saveToken(token);
      if (onUserUpdate) onUserUpdate(updatedUser);
      setEmailEditing(false);
      setEmailStep(1);
      setNewEmail("");
      setEmailCode("");
      setProfileMsg("Email updated successfully!");
      setTimeout(() => setProfileMsg(""), 4000);
    } catch (err) {
      setEmailMsg(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const currentPlan = billing?.plan || "free";
  const currentLevel = tierLevel[currentPlan] ?? 0;
  const config = planConfig[currentPlan] || planConfig.free;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {activeTab === "automations" ? (
        <Automations />
      ) : activeTab === "pipelines" ? (
        <PipelineSettings />
      ) : (
      <>
      {billingMsg && (
        <div ref={billingMsgRef} style={{
          background: billingMsg.includes("success") ? t.emeraldLight : t.amberLight,
          border: `1px solid ${billingMsg.includes("success") ? `${t.emerald}40` : `${t.amber}40`}`,
          borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13,
          color: billingMsg.includes("success") ? t.emeraldDeep : t.amber,
          display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: font.body,
        }}>
          <span>{billingMsg}</span>
          <span onClick={() => setBillingMsg("")} style={{ cursor: "pointer", fontWeight: 700, fontSize: 16 }}>&times;</span>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: responsive.settingsColumns, gap: 20 }}>
        {/* Billing & Plan */}
        <Panel padding={20} style={{ position: "relative", overflow: "visible", minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Billing & Plan</div>
            {/* Three-dot menu */}
            {billing && (
              <BillingMenu billing={billing} navigate={navigate} />
            )}
          </div>
          {billingLoading ? (
            <div style={{ fontSize: 13, color: t.textMuted, padding: 20, textAlign: "center", fontFamily: font.body }}>Loading billing info...</div>
          ) : billing ? (
            <div>
              {/* Current Plan */}
              <div style={{ background: t.emeraldLight, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: t.emerald, fontWeight: 700, letterSpacing: 1, marginBottom: 2, fontFamily: font.mono }}>CURRENT PLAN</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>
                      {billing.isFree ? "Free" : config.label}
                    </div>
                  </div>
                  {!billing.isFree && (
                    <div style={{ fontSize: 24, fontWeight: 800, color: billing.subscriptionStatus === "cancelling" ? t.amber : t.emerald, fontFamily: font.mono }}>
                      ${config.price}<span style={{ fontSize: 12, color: t.textMuted }}>/mo</span>
                    </div>
                  )}
                </div>

                {/* Free Plan Status */}
                {billing.isFree && (
                  <div style={{
                    background: t.surface, borderRadius: 8, padding: "10px 14px", marginTop: 8,
                    border: `1px solid ${t.emerald}30`,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>Free Plan</div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, marginTop: 4 }}>
                      {billing.freeSigningsRemaining ?? 0} free signing{(billing.freeSigningsRemaining ?? 0) !== 1 ? "s" : ""} remaining
                    </div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, marginTop: 4, lineHeight: 1.5 }}>
                      Clients, pipeline, and deals are free forever. Subscribe for signings, AI, and automation.
                    </div>
                  </div>
                )}

                {billing.subscriptionStatus === "cancelling" && (() => {
                  const expiresAt = billing.cancelAt || subscription?.currentPeriodEnd || null;
                  const expiresDate = expiresAt ? new Date(expiresAt) : null;
                  const daysLeft = expiresDate && !isNaN(expiresDate) ? Math.max(0, Math.ceil((expiresDate - Date.now()) / 86400000)) : 0;
                  return (
                  <div style={{ marginTop: 8 }}>
                    <Badge variant="amber">Cancelling</Badge>
                    <div style={{
                      background: t.amberLight, borderRadius: 8, padding: "14px 16px", marginTop: 8,
                      border: `1px solid ${t.amber}30`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div style={{
                          minWidth: 52, height: 52, borderRadius: 10, display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center",
                          background: t.amber + "20", border: `1.5px solid ${t.amber}40`,
                        }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: t.amber, fontFamily: font.mono, lineHeight: 1 }}>{daysLeft}</div>
                          <div style={{ fontSize: 7, fontWeight: 700, color: t.amber, fontFamily: font.mono, letterSpacing: 0.5 }}>DAY{daysLeft !== 1 ? "S" : ""}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: t.amber, fontFamily: font.body }}>
                            {expiresDate && !isNaN(expiresDate)
                              ? `Expires ${expiresDate.toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}`
                              : "Your plan is being cancelled"}
                          </div>
                          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 3 }}>
                            You have full access until then. Resume anytime to keep your plan.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })()}

                {billing.subscriptionStatus === "active" && (
                  <div style={{ marginTop: 8 }}>
                    <Badge variant="emerald">Active Subscription</Badge>
                    <div style={{
                      background: t.surface, borderRadius: 8, padding: "10px 14px", marginTop: 8,
                      border: `1px solid ${t.border}`,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: (billing.addonBalance || 0) > 0 ? 8 : 0 }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>Plan Signings</div>
                          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>
                            {billing.signingsUsed || 0} / {billing.signingsLimit || 0} used this month
                          </div>
                        </div>
                        <div style={{ width: 100 }}>
                          <ProgressBar value={((billing.signingsUsed || 0) / (billing.signingsLimit || 1)) * 100} color={(billing.signingsUsed || 0) >= (billing.signingsLimit || 1) * 0.9 ? t.coral : t.emerald} size="sm" />
                        </div>
                      </div>
                      {(billing.addonBalance || 0) > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${t.border}`, paddingTop: 8 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: t.emerald, fontFamily: font.body }}>Purchased Signings</div>
                            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>Never expires</div>
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: t.emerald, fontFamily: font.mono }}>{billing.addonBalance}</div>
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${t.border}`, paddingTop: 8, marginTop: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>Total Available</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: t.navyLight, fontFamily: font.mono }}>{billing.totalSigningsAvailable ?? 0}</div>
                      </div>
                    </div>
                  </div>
                )}

                {billing.subscriptionStatus === "past_due" && (
                  <div style={{
                    background: t.amberLight, borderRadius: 8, padding: "10px 14px", marginTop: 8,
                    border: `1px solid ${t.amber}40`, fontSize: 12, color: t.amber, fontFamily: font.body,
                  }}>
                    Payment failed. Please update your payment method.
                  </div>
                )}
              </div>

              {/* Payment Method */}
              {(billing.subscriptionStatus === "active" || billing.subscriptionStatus === "cancelling") && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>PAYMENT METHOD</div>
                  {paymentMethod ? (
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      background: t.surfaceWarm, borderRadius: 8, padding: "10px 14px", border: `1px solid ${t.border}`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          background: t.navyLight, color: "#fff", borderRadius: 4, padding: "4px 8px",
                          fontSize: 10, fontWeight: 700, fontFamily: font.mono, textTransform: "uppercase",
                        }}>{paymentMethod.brand}</div>
                        <span style={{ fontSize: 13, color: t.textPrimary, fontFamily: font.mono }}>
                          •••• {paymentMethod.last4}
                        </span>
                        <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                          Exp {paymentMethod.expMonth}/{paymentMethod.expYear}
                        </span>
                      </div>
                      <span onClick={handleManageBilling} style={{ fontSize: 11, color: t.emerald, fontWeight: 600, cursor: "pointer", fontFamily: font.body }}>Update</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>No payment method on file</div>
                  )}
                </div>
              )}

              {/* Subscription status line for active users */}
              {(billing.subscriptionStatus === "active" || billing.subscriptionStatus === "cancelling") && subscription?.cancelAtPeriodEnd && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
                  <div style={{
                    flex: 1, background: t.amberLight, borderRadius: 8, padding: "10px 14px",
                    border: `1px solid ${t.amber}30`, fontSize: 12, color: t.amber, fontFamily: font.body,
                  }}>
                    Cancels on {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </div>
                  <Button variant="primary" onClick={handleResumeSubscription} disabled={!!billingAction}>
                    {billingAction === "resuming" ? "Resuming..." : "Resume"}
                  </Button>
                </div>
              )}

              {/* Choose a Plan */}
              <div style={{ marginTop: 20, borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                    {billing.subscriptionStatus === "active" ? "Change Plan" : "Choose a Plan"}
                  </div>
                  {/* Monthly / Annual Toggle */}
                  <div style={{ display: "flex", alignItems: "center", gap: 0, background: t.pageBg, borderRadius: 99, padding: 2, border: `1px solid ${t.border}` }}>
                    <button onClick={() => setBillingInterval("monthly")} style={{
                      padding: "5px 14px", borderRadius: 99, border: "none", cursor: "pointer",
                      fontSize: 10, fontWeight: 600, fontFamily: font.body, transition: "all 0.2s",
                      background: billingInterval === "monthly" ? t.emerald : "transparent",
                      color: billingInterval === "monthly" ? "#fff" : t.textMuted,
                    }}>Monthly</button>
                    <button onClick={() => setBillingInterval("annual")} style={{
                      padding: "5px 14px", borderRadius: 99, border: "none", cursor: "pointer",
                      fontSize: 10, fontWeight: 600, fontFamily: font.body, transition: "all 0.2s",
                      background: billingInterval === "annual" ? t.emerald : "transparent",
                      color: billingInterval === "annual" ? "#fff" : t.textMuted,
                    }}>Annual <span style={{ fontSize: 8, color: billingInterval === "annual" ? "#fff" : t.emerald }}>Save 2mo</span></button>
                  </div>
                </div>

                {/* Solo Plans */}
                <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, marginBottom: 6, fontFamily: font.mono, letterSpacing: 1 }}>SOLO AGENT</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
                  {Object.entries(planFeatures).filter(([key]) => ["free", "starter", "pro"].includes(key)).map(([key, features]) => {
                    const cfg = planConfig[key];
                    const isFree = key === "free";
                    const isCurrent = isFree
                      ? (billing.isFree || billing.subscriptionStatus === "free" || (!billing.subscriptionStatus || billing.subscriptionStatus === "free") && currentPlan === "free")
                      : currentPlan === key && billing.subscriptionStatus === "active";
                    const displayPrice = cfg.price != null ? (billingInterval === "annual" && cfg.price > 0 ? Math.round(cfg.price * 10 / 12) : cfg.price) : null;
                    return (
                      <div key={key} style={{
                        background: isCurrent ? t.emeraldLight : t.surfaceWarm,
                        border: isCurrent ? `2px solid ${t.emerald}` : `1px solid ${t.border}`,
                        borderRadius: 10, padding: 14, display: "flex", flexDirection: "column",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: isCurrent ? t.emerald : t.textPrimary, fontFamily: font.body }}>{cfg.label}</div>
                          {isCurrent && <div style={{ fontSize: 8, color: t.emerald, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>CURRENT</div>}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, marginBottom: 2, fontFamily: font.mono }}>
                          ${displayPrice}{cfg.price > 0 && <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 400 }}>/mo</span>}
                        </div>
                        {billingInterval === "annual" && cfg.price > 0 && (
                          <div style={{ fontSize: 9, color: t.emerald, fontWeight: 600, marginBottom: 6, fontFamily: font.body }}>
                            ${(cfg.price * 10).toLocaleString()}/yr — Save ${(cfg.price * 2).toLocaleString()}
                          </div>
                        )}
                        <div style={{ flex: 1, marginBottom: 10 }}>
                          {features.map(f => (
                            <div key={f} style={{ fontSize: 9, color: t.textSecondary, marginBottom: 3, display: "flex", gap: 4, alignItems: "flex-start", fontFamily: font.body }}>
                              <span style={{ color: t.emerald, fontSize: 8, marginTop: 1, flexShrink: 0 }}>{"\u2713"}</span> {f}
                            </div>
                          ))}
                        </div>
                        {!isCurrent && (
                          <button onClick={() => handleUpgrade(key)} disabled={!!billingAction} style={{
                            width: "100%", padding: "8px 0", borderRadius: 8, border: "none", cursor: billingAction ? "not-allowed" : "pointer",
                            fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                            background: key === "pro" ? `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})` : t.surface,
                            color: key === "pro" ? "#fff" : t.textPrimary,
                            border: key === "pro" ? "none" : `1px solid ${t.border}`,
                            fontFamily: font.body, transition: "all 0.2s",
                          }}>
                            {billingAction === key ? "Processing..." : billing?.subscriptionStatus === "active" || billing?.isFree ? (tierLevel[key] > currentLevel ? "Upgrade" : "Downgrade") : "Subscribe"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Team Plans */}
                <div style={{ fontSize: 9, fontWeight: 700, color: t.textMuted, marginBottom: 6, fontFamily: font.mono, letterSpacing: 1 }}>TEAM</div>
                <div style={{ display: "grid", gridTemplateColumns: responsive.width < 768 ? "1fr" : "repeat(2, 1fr)", gap: 8 }}>
                  {Object.entries(planFeatures).filter(([key]) => ["team_starter", "team_pro", "agency", "enterprise"].includes(key)).map(([key, features]) => {
                    const cfg = planConfig[key];
                    const isCurrent = currentPlan === key && billing.subscriptionStatus === "active";
                    const displayPrice = cfg.price != null ? (billingInterval === "annual" ? Math.round(cfg.price * 10 / 12) : cfg.price) : null;
                    const isEnterprise = key === "enterprise";
                    return (
                      <div key={key} style={{
                        background: isCurrent ? t.emeraldLight : t.surfaceWarm,
                        border: isCurrent ? `2px solid ${t.emerald}` : `1px solid ${t.border}`,
                        borderRadius: 10, padding: 14, display: "flex", flexDirection: "column",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: isCurrent ? t.emerald : t.textPrimary, fontFamily: font.body }}>{cfg.label}</div>
                          {isCurrent && <div style={{ fontSize: 8, color: t.emerald, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>CURRENT</div>}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, marginBottom: 2, fontFamily: font.mono }}>
                          {displayPrice != null ? (
                            <>${displayPrice}<span style={{ fontSize: 10, color: t.textMuted, fontWeight: 400 }}>/mo</span></>
                          ) : (
                            <span style={{ fontSize: 14, fontWeight: 700 }}>Contact Sales</span>
                          )}
                        </div>
                        {billingInterval === "annual" && cfg.price != null && (
                          <div style={{ fontSize: 9, color: t.emerald, fontWeight: 600, marginBottom: 6, fontFamily: font.body }}>
                            ${(cfg.price * 10).toLocaleString()}/yr — Save ${(cfg.price * 2).toLocaleString()}
                          </div>
                        )}
                        <div style={{ flex: 1, marginBottom: 10 }}>
                          {features.map(f => (
                            <div key={f} style={{ fontSize: 9, color: t.textSecondary, marginBottom: 3, display: "flex", gap: 4, alignItems: "flex-start", fontFamily: font.body }}>
                              <span style={{ color: t.emerald, fontSize: 8, marginTop: 1, flexShrink: 0 }}>{"\u2713"}</span> {f}
                            </div>
                          ))}
                        </div>
                        {!isCurrent && (
                          <button onClick={() => isEnterprise ? navigate("/contact?plan=enterprise") : handleUpgrade(key)} disabled={!!billingAction && !isEnterprise} style={{
                            width: "100%", padding: "8px 0", borderRadius: 8, border: "none", cursor: billingAction && !isEnterprise ? "not-allowed" : "pointer",
                            fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                            background: isEnterprise ? t.gold : t.surface,
                            color: isEnterprise ? "#fff" : t.textPrimary,
                            border: isEnterprise ? "none" : `1px solid ${t.border}`,
                            fontFamily: font.body, transition: "all 0.2s",
                          }}>
                            {isEnterprise ? "Contact Sales" : billingAction === key ? "Processing..." : tierLevel[key] > currentLevel ? "Upgrade" : "Downgrade"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Signing Add-Ons — only for paid users */}
              {billing.subscriptionStatus === "active" && (
                <div style={{ marginTop: 20, borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 4, fontFamily: font.body }}>Need More Signings?</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 12, fontFamily: font.body }}>One-time purchase. Added to your balance instantly. Never expires.</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {[
                      { key: "single", name: "1 Signing", price: "$9", per: "$9/signing" },
                      { key: "pack_10", name: "10 Signings", price: "$75", per: "$7.50/signing", save: "Save 17%" },
                      { key: "pack_25", name: "25 Signings", price: "$175", per: "$7/signing", save: "Save 22%" },
                    ].map(p => (
                      <div key={p.key} style={{
                        background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 10,
                        padding: 12, textAlign: "center",
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, marginBottom: 4 }}>{p.name}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: t.navyLight, fontFamily: font.mono }}>{p.price}</div>
                        <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.body, marginTop: 2 }}>{p.per}</div>
                        {p.save && <div style={{ fontSize: 8, color: t.emerald, fontWeight: 700, marginTop: 4, fontFamily: font.body }}>{p.save}</div>}
                        <button onClick={() => setSigningPurchaseConfirm({ key: p.key, name: p.name, price: p.price, quantity: p.key === "single" ? 1 : p.key === "pack_10" ? 10 : 25 })} style={{
                          width: "100%", marginTop: 8, padding: "6px 0", borderRadius: 6, border: "none",
                          background: t.navyLight, color: "#fff", fontSize: 9, fontWeight: 700,
                          letterSpacing: 1, textTransform: "uppercase", cursor: "pointer",
                          fontFamily: font.body,
                        }}>Buy Now</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: t.textMuted, padding: 20, textAlign: "center", fontFamily: font.body }}>Unable to load billing info</div>
          )}
        </Panel>

        {/* Agent Profile */}
        <Panel padding={20} style={{ position: "relative", overflow: "visible" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Agent Profile</div>
          {/* Three-dot menu */}
          <div style={{ position: "absolute", top: 12, right: 12 }}>
            <div onClick={() => setMenuOpen(!menuOpen)} style={{
              width: 30, height: 30, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", background: menuOpen ? t.surfaceWarm : "transparent", border: `1px solid ${menuOpen ? t.border : "transparent"}`,
              transition: "all 0.15s",
            }}
              onMouseEnter={e => { if (!menuOpen) e.currentTarget.style.background = t.surfaceWarm; }}
              onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = "transparent"; }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="3" r="1.5" fill={t.textSecondary} />
                <circle cx="8" cy="8" r="1.5" fill={t.textSecondary} />
                <circle cx="8" cy="13" r="1.5" fill={t.textSecondary} />
              </svg>
            </div>
            {menuOpen && (
              <>
                <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setMenuOpen(false)} />
                <div style={{
                  position: "absolute", top: 36, right: 0, background: t.surface,
                  border: `1.5px solid ${t.border}`, borderRadius: 10, padding: "6px 0",
                  minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 100,
                  animation: "scaleIn 0.15s ease",
                }}>
                  {[
                    { label: "Edit Profile", icon: "✏️", action: () => { setEditing(true); setMenuOpen(false); } },
                    { label: "Change Email", icon: "✉️", action: () => { setEmailEditing(true); setEmailStep(1); setNewEmail(""); setEmailCode(""); setEmailMsg(""); setMenuOpen(false); } },
                    { label: "Upgrade Plan", icon: "⭐", action: () => { navigate("/settings"); setMenuOpen(false); } },
                    { label: "Change Password", icon: "🔒", action: () => { setMenuOpen(false); } },
                    { divider: true },
                    { label: "Logout", icon: "🚪", action: () => { setMenuOpen(false); }, danger: true },
                  ].map((item, i) => item.divider ? (
                    <div key={i} style={{ height: 1, background: t.border, margin: "4px 0" }} />
                  ) : (
                    <div key={i} onClick={item.action} style={{
                      padding: "9px 16px", display: "flex", alignItems: "center", gap: 10,
                      cursor: "pointer", fontSize: 13, fontFamily: font.body,
                      color: item.danger ? t.coral : t.textPrimary,
                      transition: "background 0.1s",
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <span style={{ fontSize: 14 }}>{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {profileMsg && (
            <div style={{
              background: (profileMsg.includes("success") || profileMsg.includes("updated") || profileMsg.includes("Updated")) ? t.emeraldLight : t.coralLight,
              border: `1px solid ${(profileMsg.includes("success") || profileMsg.includes("updated") || profileMsg.includes("Updated")) ? `${t.emerald}40` : `${t.coral}40`}`,
              borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12,
              color: (profileMsg.includes("success") || profileMsg.includes("updated") || profileMsg.includes("Updated")) ? t.emeraldDeep : t.coral,
              display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: font.body,
            }}>
              <span>{profileMsg}</span>
              <span onClick={() => setProfileMsg("")} style={{ cursor: "pointer", fontWeight: 700, fontSize: 14 }}>&times;</span>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ position: "relative", cursor: "pointer" }} onClick={() => document.getElementById("photo-upload").click()}>
              {user?.photo_url ? (
                <img src={user.photo_url} alt="" style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <Avatar name={profileForm.name || user?.name || "Agent"} size={56} />
              )}
              <div style={{
                position: "absolute", bottom: -2, right: -2, width: 20, height: 20, borderRadius: "50%",
                background: t.emerald, display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #fff", fontSize: 10, color: "#fff",
              }}>+</div>
              <input id="photo-upload" type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    setProfileMsg("Uploading...");
                    const { photoUrl } = await uploadPhoto(file);
                    if (onUserUpdate) onUserUpdate({ ...user, photo_url: photoUrl });
                    setProfileMsg("Photo updated!");
                    setTimeout(() => setProfileMsg(""), 3000);
                  } catch (err) { setProfileMsg(err.message || "Upload failed"); }
                  e.target.value = "";
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>{profileForm.name || user?.name || "Agent"}</div>
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>{user?.email || "agent@realtyflows.com"}</div>
            </div>
          </div>
          {editing ? (
            <>
              {[
                { key: "name", label: "Full Name" },
                { key: "phone", label: "Phone" },
                { key: "brokerage", label: "Brokerage" },
              ].map(f => (
                <div key={f.key} style={{ marginBottom: 12 }}>
                  <Input label={f.label} value={profileForm[f.key]} onChange={e => setProfileForm({ ...profileForm, [f.key]: e.target.value })} />
                </div>
              ))}
              <div style={{ marginBottom: 12 }}>
                <Input label="Email" value={user?.email || ""} disabled style={{ opacity: 0.5 }} />
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <Button variant="primary" onClick={handleSaveProfile} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Button>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </>
          ) : (
            <div>
              {[
                { label: "Full Name", value: user?.name },
                { label: "Email", value: user?.email },
                { label: "Phone", value: user?.phone },
                { label: "Brokerage", value: user?.brokerage },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${t.border}`, fontSize: 13, fontFamily: font.body }}>
                  <span style={{ color: t.textMuted }}>{label}</span>
                  <span style={{ color: t.textPrimary, fontWeight: 500 }}>{value || "-"}</span>
                </div>
              ))}
            </div>
          )}

          {/* Change Email Flow */}
          {emailEditing && (
            <div style={{ marginTop: 16, padding: 16, background: t.surfaceWarm, borderRadius: 10, border: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.body }}>Change Email</div>
              {emailMsg && (
                <div style={{
                  background: t.coralLight, border: `1px solid ${t.coral}40`, borderRadius: 8,
                  padding: "8px 12px", marginBottom: 12, fontSize: 12, color: t.coral, fontFamily: font.body,
                }}>
                  {emailMsg}
                </div>
              )}
              {emailStep === 1 ? (
                <>
                  <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 10, fontFamily: font.body }}>
                    Current: <span style={{ color: t.textPrimary, fontWeight: 500 }}>{user?.email}</span>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Input label="New Email" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="new@email.com" />
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button variant="primary" onClick={handleSendEmailCode} disabled={emailLoading || !newEmail}>
                      {emailLoading ? "Sending..." : "Send Code"}
                    </Button>
                    <Button variant="outline" onClick={() => setEmailEditing(false)}>Cancel</Button>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 12, color: t.textSecondary, marginBottom: 14, fontFamily: font.body, lineHeight: 1.5 }}>
                    Enter the 6-digit code sent to<br />
                    <span style={{ fontWeight: 600, color: t.textPrimary }}>{newEmail}</span>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <CodeInput value={emailCode} onChange={setEmailCode} length={6} disabled={emailLoading} />
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                    <Button variant="primary" onClick={handleVerifyAndChangeEmail} disabled={emailLoading || emailCode.length !== 6}>
                      {emailLoading ? "Updating..." : "Verify & Update"}
                    </Button>
                    <Button variant="outline" onClick={() => setEmailEditing(false)}>Cancel</Button>
                  </div>
                  <div style={{ marginTop: 10, fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                    {emailCooldown > 0 ? (
                      <span>Resend in {emailCooldown}s</span>
                    ) : (
                      <span onClick={handleSendEmailCode} style={{ color: t.emerald, cursor: "pointer", fontWeight: 600 }}>
                        Resend code
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: responsive.settingsColumns, gap: 20, marginTop: 20 }}>
        <Panel padding={20}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Integrations</div>
          {[
            { name: "Gmail", desc: "Send & receive email", connected: true, color: t.coral },
            { name: "Google Calendar", desc: "Sync appointments", connected: true, color: t.sky },
            { name: "Zillow", desc: "Import clients automatically", connected: true, color: t.sky },
            { name: "Facebook Ads", desc: "Capture clients from ads", connected: false, color: t.sky },
            { name: "DocuSign", desc: "Send contracts for e-signature", connected: true, color: t.amber },
            { name: "Outlook Calendar", desc: "Sync appointments", connected: false, color: t.sky },
          ].map(int => (
            <div key={int.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${t.border}` }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: int.color + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: int.color }}>{int.name[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{int.name}</div>
                <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{int.desc}</div>
              </div>
              <Badge variant={int.connected ? "emerald" : "sky"}>{int.connected ? "Connected" : "Connect"}</Badge>
            </div>
          ))}
        </Panel>

        <Panel padding={20}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Notifications</div>
          {[
            { key: "newClient", label: "New client alerts", desc: "Get notified when a new client arrives" },
            { key: "hotClient", label: "Hot client alerts", desc: "Score > 80 instant notification" },
            { key: "appointments", label: "Appointment reminders", desc: "24h before scheduled showings" },
            { key: "weekly", label: "Weekly digest", desc: "Performance summary every Monday" },
            { key: "aiAlerts", label: "AI conversation alerts", desc: "When AI needs human intervention" },
          ].map(n => (
            <div key={n.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${t.border}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{n.label}</div>
                <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{n.desc}</div>
              </div>
              <Toggle checked={notifications[n.key]} onChange={() => setNotifications(prev => ({ ...prev, [n.key]: !prev[n.key] }))} />
            </div>
          ))}
        </Panel>
      </div>

      <Panel padding={20} style={{ marginTop: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Client Capture Sources</div>
        <div style={{ display: "grid", gridTemplateColumns: responsive.captureColumns, gap: 12 }}>
          {[
            { name: "Website Forms", desc: "Embed on your site", active: true, icon: "W", color: t.emerald },
            { name: "Facebook Ads", desc: "Auto-import from ads", active: true, icon: "f", color: t.sky },
            { name: "Zillow", desc: "Zillow client sync", active: true, icon: "Z", color: t.sky },
            { name: "Instagram", desc: "DM client capture", active: false, icon: "IG", color: t.coral },
            { name: "Manual / CSV", desc: "Import spreadsheets", active: true, icon: "CSV", color: t.amber },
          ].map(src => (
            <div key={src.name} style={{ background: t.surfaceWarm, border: `1px solid ${src.active ? src.color + "30" : t.border}`, borderRadius: 10, padding: 16, textAlign: "center" }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, margin: "0 auto 10px", background: src.color + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: src.color }}>{src.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, marginBottom: 2, fontFamily: font.body }}>{src.name}</div>
              <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 10, fontFamily: font.body }}>{src.desc}</div>
              <Badge variant={src.active ? "emerald" : "sky"}>{src.active ? "Active" : "Setup"}</Badge>
            </div>
          ))}
        </div>
      </Panel>
      </>
      )}

      {/* Plan Switch Confirmation Modal */}
      {switchConfirm && createPortal(
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 220, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: t.surface, borderRadius: 16, padding: 28, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>
              {switchConfirm.plan === "free" ? "Downgrade to Free" : switchConfirm.isSwitch ? (switchConfirm.isUpgrade ? "Upgrade" : "Downgrade") + " to " + switchConfirm.label : "Subscribe to " + switchConfirm.label}
            </div>
            <div style={{ fontSize: 13, color: t.textSecondary, marginBottom: 20, fontFamily: font.body, lineHeight: 1.6 }}>
              {switchConfirm.plan === "free"
                ? <>Your <strong>{planConfig[currentPlan]?.label}</strong> subscription will be cancelled at the end of your billing period.</>
                : switchConfirm.isSwitch
                  ? <>Switch from <strong>{planConfig[currentPlan]?.label}</strong> to <strong>{switchConfirm.label}</strong></>
                  : <>You're about to subscribe to the <strong>{switchConfirm.label}</strong> plan</>
              }
            </div>

            <div style={{
              background: switchConfirm.plan === "free" ? "#fef2f2" : t.emeraldLight,
              border: `1px solid ${switchConfirm.plan === "free" ? "#fca5a530" : t.emerald + "30"}`,
              borderRadius: 10, padding: 16, marginBottom: 20,
            }}>
              {switchConfirm.plan === "free" ? (
                <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.6 }}>
                  You'll keep access to your current plan until the end of this billing period. After that, you'll be on the Free plan with unlimited CRM and 1 signing. Your data will be preserved.
                </div>
              ) : (
                <>
                  {switchConfirm.isSwitch && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Current</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.mono }}>${switchConfirm.oldPrice}/mo</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>{switchConfirm.isSwitch ? "New" : "Price"}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.emerald, fontFamily: font.mono }}>${switchConfirm.newPrice}/mo</span>
                  </div>
                  <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 11, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.6 }}>
                      {switchConfirm.isSwitch
                        ? (switchConfirm.isUpgrade
                            ? `You'll be charged a prorated amount of ~$${Math.abs(switchConfirm.newPrice - switchConfirm.oldPrice)} for the remainder of this billing period.`
                            : `You'll receive a credit of ~$${Math.abs(switchConfirm.oldPrice - switchConfirm.newPrice)} on your next invoice.`)
                        : `You'll be redirected to Stripe to enter payment details. You can cancel anytime.`
                      }
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setSwitchConfirm(null)} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${t.border}`,
                background: t.surface, color: t.textPrimary, fontSize: 12, fontWeight: 600,
                fontFamily: font.body, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={handleConfirmSwitch} disabled={!!billingAction} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                background: switchConfirm.plan === "free" ? "#ef4444" : `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
                color: "#fff", fontSize: 12, fontWeight: 600,
                fontFamily: font.body, cursor: billingAction ? "not-allowed" : "pointer",
                opacity: billingAction ? 0.7 : 1,
              }}>{billingAction ? "Processing..." : switchConfirm.plan === "free" ? "Confirm Downgrade" : (switchConfirm.isSwitch ? `Confirm ${switchConfirm.isUpgrade ? "Upgrade" : "Downgrade"}` : `Subscribe — $${switchConfirm.newPrice}/mo`)}</button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Signing Purchase Confirmation Modal */}
      {signingPurchaseConfirm && createPortal(
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 220, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: t.surface, borderRadius: 16, padding: 28, maxWidth: 380, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>
              Purchase Signings
            </div>
            <div style={{ fontSize: 13, color: t.textSecondary, marginBottom: 20, fontFamily: font.body }}>
              Buy <strong>{signingPurchaseConfirm.quantity} signing{signingPurchaseConfirm.quantity > 1 ? "s" : ""}</strong> for <strong>{signingPurchaseConfirm.price}</strong>
            </div>
            <div style={{
              background: t.surfaceWarm, border: `1px solid ${t.border}`,
              borderRadius: 10, padding: 16, marginBottom: 20,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12, fontFamily: font.body }}>
                <span style={{ color: t.textMuted }}>Item</span>
                <span style={{ color: t.textPrimary, fontWeight: 600 }}>{signingPurchaseConfirm.name}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12, fontFamily: font.body }}>
                <span style={{ color: t.textMuted }}>Amount</span>
                <span style={{ color: t.textPrimary, fontWeight: 700, fontFamily: font.mono }}>{signingPurchaseConfirm.price}</span>
              </div>
              <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 8, fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                One-time charge. Signings added instantly. Never expire.
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setSigningPurchaseConfirm(null)} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${t.border}`,
                background: t.surface, color: t.textPrimary, fontSize: 12, fontWeight: 600,
                fontFamily: font.body, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={async () => {
                try {
                  setBillingAction(signingPurchaseConfirm.key);
                  const { quantity } = await purchaseSignings(signingPurchaseConfirm.key);
                  const updated = await getBillingStatus();
                  setBilling(updated);
                  if (onBillingChange) onBillingChange();
                  setBillingMsg(`${quantity} signing${quantity > 1 ? "s" : ""} added to your balance!`);
                  setSigningPurchaseConfirm(null);
                } catch (err) {
                  setBillingMsg(err.message || "Purchase failed");
                  setSigningPurchaseConfirm(null);
                } finally { setBillingAction(""); }
              }} disabled={!!billingAction} style={{
                flex: 1, padding: "10px 0", borderRadius: 8, border: "none",
                background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
                color: "#fff", fontSize: 12, fontWeight: 600,
                fontFamily: font.body, cursor: billingAction ? "not-allowed" : "pointer",
                opacity: billingAction ? 0.7 : 1,
              }}>{billingAction ? "Processing..." : `Pay ${signingPurchaseConfirm.price}`}</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
